/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   JobSchedules.tsx
 *     Feature         :   114-job-infrastructure; tenant-specific schedule (owner, 2026-10-30)
 *     Description     :   The signed-in organization's collection schedule, read only. It is
 *                         dbo.worker_schedules, what the brain dispatches from, and the brain owns
 *                         it (Spec 278), so nothing here changes it.
 *     Constitution    :   v1.11.0 | Principle XXXVIII (Specification Testing Standards)
 *     Author          :   Peter Dobler
 *     Created         :   2026-01-28
 * =========================================================================================
 */

import React from 'react';
import { RefreshCw } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

import { Card, DataTable, Badge, Alert, Spinner } from '../../components/ui';
import type { DataTableColumn } from '../../components/ui';
import type { JobSchedule, JobScheduleCadence } from '../../services/jobService';
import { formatEastern } from '../../utils/dateFormatter';
import { RUN_STATE_LABEL, useTenantJobSchedule } from './useTenantJobSchedule';

const CADENCE_LABELS: Record<JobScheduleCadence, string> = {
  daily: 'Daily',
  '2x-daily': 'Twice a day',
  '4x-daily': 'Four times a day',
  hourly: 'Hourly',
  '30-min': 'Every 30 minutes',
  '15-min': 'Every 15 minutes',
  '5-min': 'Every 5 minutes',
};

/** Icon-button styling (token-driven; replaces MUI IconButton). */
const iconBtn =
  'inline-flex items-center justify-center rounded-[var(--radius-md)] p-1.5 ' +
  'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)] hover:text-[var(--color-text-primary)]';

const COLUMNS: DataTableColumn<JobSchedule>[] = [
  {
    key: 'job_name',
    header: 'Job',
    cellClassName: 'font-medium text-[var(--color-text-primary)]',
    render: (s) => s.job_name,
  },
  {
    key: 'cadence',
    header: 'Runs',
    render: (s) => CADENCE_LABELS[s.cadence] ?? s.cadence,
  },
  {
    key: 'last_dispatched_at',
    header: 'Last sent to worker',
    sortValue: (s) => s.last_dispatched_at ?? '',
    render: (s) =>
      s.last_dispatched_at ? formatDistanceToNow(new Date(s.last_dispatched_at), { addSuffix: true }) : 'Never',
  },
  {
    key: 'next_due_at',
    header: 'Next run',
    sortValue: (s) => s.next_due_at ?? '',
    render: (s) => (s.next_due_at ? formatEastern(s.next_due_at) : 'Not scheduled'),
  },
  {
    key: 'state',
    header: 'State',
    sortable: false,
    render: (s) => {
      if (s.run_state === 'requested' && s.run_requested_by) {
        return <Badge variant="info">Run requested by {s.run_requested_by}</Badge>;
      }
      if (s.run_state) {
        return <Badge variant={s.run_state === 'running' ? 'primary' : 'info'}>{RUN_STATE_LABEL[s.run_state]}</Badge>;
      }
      return s.is_active ? <Badge variant="success">Active</Badge> : <Badge variant="default">Off</Badge>;
    },
  },
];

export const JobSchedules: React.FC = () => {
  const { data, isLoading: loading, error: readError, refetch } = useTenantJobSchedule();
  const error = readError ? (readError instanceof Error ? readError.message : 'The schedule could not be read') : null;

  const schedules = data?.schedules ?? [];

  return (
    <div>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h6 className="text-lg font-semibold text-[var(--color-text-primary)]">
            Collection schedule ({schedules.length})
          </h6>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
            Your organization&apos;s schedule. The platform sets it, so it can&apos;t be changed here.
          </p>
        </div>
        <button className={iconBtn} onClick={() => void refetch()} aria-label="Refresh schedule">
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      {error && (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      )}
      {data && !data.dispatchEnabled && (
        <Alert variant="warning" className="mb-4">
          Collection is switched off on this platform, so nothing runs on this schedule until it is switched back on.
        </Alert>
      )}
      {data && !data.workerRegistered && (
        <Alert variant="warning" className="mb-4">
          No worker is registered for your organization, so nothing runs on this schedule.
        </Alert>
      )}

      {loading && !data ? (
        <div className="flex justify-center py-6">
          <Spinner size="md" />
        </div>
      ) : (
        <Card padding="none" className="overflow-hidden">
          <DataTable
            columns={COLUMNS}
            rows={schedules}
            rowKey={(s) => s.id}
            minWidth="44rem"
            emptyMessage="Your organization has no collection schedule yet. One is created when its worker registers."
          />
        </Card>
      )}
    </div>
  );
};

export default JobSchedules;
